import { LitElement, html } from 'lit';

/**
 * @component po-reference-card
 * @description Shared PO reference card — displays existing PO reference or input form.
 * Used by both quote-pending-payment and quote-confirmed pages.
 *
 * @property {String} quoteReference - Quote reference number (for contact mailto)
 * @property {String} custRef - Existing PO reference from API (empty = show input form)
 *
 * @fires {CustomEvent} submit-po - User submitted a PO reference { detail: { custRef: '...' } }
 */
class PoReferenceCard extends LitElement {
    static properties = {
        quoteReference: { type: String },
        custRef: { type: String },
        // Internal state
        poReference: { type: String, state: true },
        isSubmittingPO: { type: Boolean, state: true },
        _dontHavePo: { type: Boolean, state: true },
    };

    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteReference = '';
        this.custRef = '';
        this.poReference = '';
        this.isSubmittingPO = false;
        this._dontHavePo = false;
    }

    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('custRef')) {
            this.isSubmittingPO = false;
        }
    }

    _handleSubmitPO() {
        if (!this.poReference?.trim()) return;
        this.isSubmittingPO = true;
        this.dispatchEvent(
            new CustomEvent('submit-po', {
                detail: { custRef: this.poReference.trim() },
                bubbles: true,
                composed: true,
            })
        );
    }

    render() {
        return html`
            <div class="card bg-base-200">
                <div class="card-body">
                    <h3 class="card-title text-xl mb-2">Purchase Order Reference</h3>
                    ${this.custRef
                        ? html`
                              <p class="text-sm text-gray-600">
                                  PO Reference:
                                  <strong>${this.custRef}</strong>
                              </p>
                          `
                        : html`
                              <p class="text-sm text-gray-600 mb-4">
                                  Optionally attach a purchase order reference to this quote.
                              </p>
                              <div class="flex flex-col sm:flex-row gap-3">
                                  <input
                                      type="text"
                                      .value="${this.poReference}"
                                      @input="${(e) => (this.poReference = e.target.value)}"
                                      placeholder="e.g. PO123456"
                                      class="input input-bordered flex-1"
                                      ?disabled="${this.isSubmittingPO || this._dontHavePo}"
                                  />
                                  <button
                                      @click="${this._handleSubmitPO}"
                                      class="btn btn-primary shrink-0"
                                      ?disabled="${!this.poReference?.trim() ||
                                      this.isSubmittingPO ||
                                      this._dontHavePo}"
                                  >
                                      ${this.isSubmittingPO
                                          ? html`<span
                                                    class="loading loading-spinner loading-sm"
                                                ></span>
                                                Saving...`
                                          : 'Save PO Reference'}
                                  </button>
                              </div>
                              <label class="flex items-center gap-2 cursor-pointer mt-3">
                                  <input
                                      type="checkbox"
                                      .checked="${this._dontHavePo}"
                                      @change="${(e) => {
                                          this._dontHavePo = e.target.checked;
                                          if (this._dontHavePo) this.poReference = '';
                                      }}"
                                      class="checkbox checkbox-sm checkbox-primary"
                                  />
                                  <span class="text-sm text-gray-600">I don't have my PO yet</span>
                              </label>
                              ${this._dontHavePo
                                  ? html`<p class="text-xs text-gray-500 mt-2">
                                        We will remind you by email 5 days before your invoice is
                                        generated.
                                    </p>`
                                  : ''}
                              <p class="text-xs text-gray-500 mt-2">
                                  Having trouble?
                                  <a
                                      href="mailto:info@reach.nz?subject=Quote%20${this
                                          .quoteReference}%20-%20PO%20Reference"
                                      class="link"
                                      >Contact us</a
                                  >
                              </p>
                          `}
                </div>
            </div>
        `;
    }
}

customElements.define('po-reference-card', PoReferenceCard);
